import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useVerbTrainer } from '../hooks/useVerbTrainer';
import type { DutchVerb, VerbTense } from '@homeapp/shared';
import { TENSE_LABELS } from '@homeapp/shared';
import ConjugationExercise from '../components/verbs/ConjugationExercise';
import IrregularVerbExercise from '../components/verbs/IrregularVerbExercise';
import VerbEntryTool from '../components/verbs/VerbEntryTool';
import VerbList from '../components/verbs/VerbList';
import WordExplainer, { useWordExplainer } from '../components/verbs/WordExplainer';

type Tab = 'practice' | 'manage';
type Phase = 'ready' | 'exercise' | 'feedback';

export default function VerbTrainerPage() {
  const { t } = useTranslation();
  const {
    exercise, progressOverview, verbs, loading, exerciseLoading, error, lastResult,
    fetchVerbs, fetchProgress, nextExercise, submitAnswer,
    generateVerb, saveVerb, deleteVerb, explainWord, setError,
  } = useVerbTrainer();

  const [tab, setTab] = useState<Tab>('practice');
  const [phase, setPhase] = useState<Phase>('ready');
  const [sessionStats, setSessionStats] = useState({ correct: 0, incorrect: 0, total: 0 });
  const { modalWord, modalExplanation, modalLoading, openExplainer, closeExplainer } = useWordExplainer(explainWord);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  useEffect(() => {
    if (tab === 'manage') fetchVerbs();
  }, [tab, fetchVerbs]);

  const handleStartPractice = async () => {
    setSessionStats({ correct: 0, incorrect: 0, total: 0 });
    await nextExercise();
    setPhase('exercise');
  };

  const [userAnswer, setUserAnswer] = useState('');

  const handleSubmitAnswer = async (answer: string) => {
    if (!exercise) return;
    setUserAnswer(answer);
    const result = await submitAnswer(
      exercise.verb.verbId,
      answer,
      exercise.exerciseType,
      exercise.person,
      exercise.tenseQuestion,
    );
    if (result) {
      setSessionStats(prev => ({
        correct: prev.correct + (result.correct ? 1 : 0),
        incorrect: prev.incorrect + (result.correct ? 0 : 1),
        total: prev.total + 1,
      }));
      setPhase('feedback');
    }
  };

  const handleNext = async () => {
    await nextExercise();
    setPhase('exercise');
  };

  const handleStop = () => {
    setPhase('ready');
    fetchProgress();
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">📝 {t('verbs.title', 'Verb Trainer')}</h1>
        <p className="text-gray-600 text-sm mt-1">Dutch verb conjugation & irregular verbs</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6">
        <TabButton active={tab === 'practice'} onClick={() => setTab('practice')}>
          🎯 Practice
        </TabButton>
        <TabButton active={tab === 'manage'} onClick={() => setTab('manage')}>
          📚 Manage Verbs
        </TabButton>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">✕</button>
        </div>
      )}

      {/* Practice Tab */}
      {tab === 'practice' && (
        <>
          {phase === 'ready' && (
            <ReadyScreen
              progressOverview={progressOverview}
              loading={loading}
              onStart={handleStartPractice}
              exerciseLoading={exerciseLoading}
            />
          )}

          {phase === 'exercise' && exercise && (
            <div className="space-y-4">
              {/* Session counter */}
              <div className="text-center text-sm text-gray-500">
                Exercise #{sessionStats.total + 1} · ✓ {sessionStats.correct} · ✗ {sessionStats.incorrect}
              </div>

              {exercise.exerciseType === 'conjugation' && exercise.person && (
                <ConjugationExercise
                  verb={exercise.verb}
                  person={exercise.person}
                  onSubmit={handleSubmitAnswer}
                  onExplain={openExplainer}
                />
              )}

              {exercise.exerciseType === 'irregular' && exercise.tenseQuestion && (
                <IrregularVerbExercise
                  verb={exercise.verb}
                  tenseQuestion={exercise.tenseQuestion}
                  sentenceWithBlank={exercise.sentenceWithBlank}
                  onSubmit={handleSubmitAnswer}
                  onExplain={openExplainer}
                />
              )}

              {exerciseLoading && (
                <div className="text-center text-gray-500 py-8">Loading exercise...</div>
              )}

              <div className="text-center">
                <button onClick={handleStop} className="text-sm text-gray-500 hover:text-gray-700 underline">
                  Stop practicing
                </button>
              </div>

              <WordExplainer onExplain={explainWord} />
            </div>
          )}

          {phase === 'feedback' && lastResult && (
            <FeedbackScreen
              result={lastResult}
              exercise={exercise}
              userAnswer={userAnswer}
              onNext={handleNext}
              onStop={handleStop}
              sessionStats={sessionStats}
            />
          )}
        </>
      )}

      {/* Manage Tab */}
      {tab === 'manage' && (
        <div className="space-y-6">
          <VerbEntryTool
            onGenerate={generateVerb}
            onSave={async (verb) => {
              const result = await saveVerb(verb);
              if (result) fetchVerbs();
              return result;
            }}
          />
          <hr className="border-gray-200" />
          <VerbList
            verbs={verbs}
            onDelete={async (id) => {
              await deleteVerb(id);
              fetchVerbs();
            }}
            loading={loading}
          />
        </div>
      )}

      {/* Explanation Modal */}
      {modalWord && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={closeExplainer}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">💡 {modalWord}</h3>
              <button onClick={closeExplainer} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            {modalLoading ? (
              <div className="text-center text-gray-500 py-4">Looking up...</div>
            ) : modalExplanation ? (
              <div className="space-y-3">
                {modalExplanation.translation && (
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <div className="text-lg font-semibold text-blue-900">{modalExplanation.translation}</div>
                  </div>
                )}
                <div>
                  <div className="text-sm font-medium text-blue-700">🇳🇱 Nederlands (A2):</div>
                  <p className="text-gray-700 mt-1">{modalExplanation.dutch}</p>
                </div>
                <div>
                  <div className="text-sm font-medium text-blue-700">🇬🇧 English:</div>
                  <p className="text-gray-700 mt-1">{modalExplanation.english}</p>
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-4">Could not find explanation</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
        active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-800'
      }`}
    >
      {children}
    </button>
  );
}

function ReadyScreen({ progressOverview, loading, onStart, exerciseLoading }: {
  progressOverview: { totalVerbs: number; practiced: number; totalCorrect: number; totalIncorrect: number; accuracy: number } | null;
  loading: boolean;
  onStart: () => void;
  exerciseLoading: boolean;
}) {
  return (
    <div className="space-y-6">
      {/* Progress overview */}
      {progressOverview && (
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 space-y-4">
          <h3 className="font-semibold text-gray-800">Your Progress</h3>
          <div className="grid grid-cols-2 gap-3">
            <StatCard label="Verbs" value={`${progressOverview.practiced}/${progressOverview.totalVerbs}`} />
            <StatCard label="Accuracy" value={`${progressOverview.accuracy}%`} />
            <StatCard label="Correct" value={String(progressOverview.totalCorrect)} color="text-green-600" />
            <StatCard label="Incorrect" value={String(progressOverview.totalIncorrect)} color="text-red-600" />
          </div>
        </div>
      )}

      {loading && <div className="text-center text-gray-500">Loading progress...</div>}

      {/* Start button */}
      <button
        onClick={onStart}
        disabled={exerciseLoading || (progressOverview?.totalVerbs === 0)}
        className="w-full bg-purple-600 text-white py-4 rounded-2xl text-lg font-semibold hover:bg-purple-700 disabled:opacity-50 transition-colors shadow-lg"
      >
        {exerciseLoading ? 'Loading...' : 'Start Practice 🚀'}
      </button>

      {progressOverview?.totalVerbs === 0 && (
        <p className="text-center text-sm text-gray-500">
          No verbs in the database yet. Go to "Manage Verbs" to add some!
        </p>
      )}
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="bg-white/60 rounded-xl p-3 text-center">
      <div className={`text-xl font-bold ${color || 'text-gray-900'}`}>{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

function FeedbackScreen({ result, exercise, userAnswer, onNext, onStop, sessionStats }: {
  result: { correct: boolean; expectedAnswer: string; feedback: string };
  exercise: { verb: DutchVerb; exerciseType: string; person?: string; tenseQuestion?: string; sentenceWithBlank?: string } | null;
  userAnswer: string;
  onNext: () => void;
  onStop: () => void;
  sessionStats: { correct: number; incorrect: number; total: number };
}) {
  return (
    <div className="space-y-6">
      {/* Result */}
      <div className={`rounded-2xl p-6 ${result.correct ? 'bg-green-50' : 'bg-red-50'}`}>
        <div className="text-center">
          <div className="text-4xl mb-2">{result.correct ? '✅' : '❌'}</div>
          <div className={`text-xl font-bold ${result.correct ? 'text-green-800' : 'text-red-800'}`}>
            {result.correct ? 'Correct!' : 'Incorrect'}
          </div>
        </div>

        {!result.correct && exercise && (
          <div className="mt-4 space-y-3">
            {/* Verb context */}
            <div className="text-center text-gray-700 font-medium">
              {exercise.verb.infinitive} ({exercise.verb.english})
            </div>

            {/* What was asked */}
            <div className="text-center text-sm text-gray-500">
              {exercise.exerciseType === 'conjugation' && exercise.person && (
                <span>Present tense — <strong>{exercise.person}</strong></span>
              )}
              {exercise.exerciseType === 'irregular' && exercise.tenseQuestion && (
                <span>{TENSE_LABELS[exercise.tenseQuestion as VerbTense]}</span>
              )}
            </div>

            {/* Your answer vs correct */}
            <div className="bg-white/60 rounded-xl p-4 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-red-500 font-medium text-sm w-20">You said:</span>
                <span className="text-red-700 line-through">{userAnswer}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-600 font-medium text-sm w-20">Correct:</span>
                <span className="text-green-800 font-semibold">{result.expectedAnswer}</span>
              </div>
            </div>

            {/* Example sentence filled in */}
            {exercise.sentenceWithBlank && (
              <div className="bg-white/60 rounded-xl p-3 text-center">
                <div className="text-xs text-gray-500 mb-1">Example:</div>
                <div className="text-gray-700 italic">
                  {exercise.sentenceWithBlank.replace(/___/g, result.expectedAnswer)}
                </div>
              </div>
            )}

            {/* Full conjugation / forms for reference */}
            {exercise.exerciseType === 'conjugation' && (
              <div className="bg-white/60 rounded-xl p-3">
                <div className="text-xs text-gray-500 mb-2 text-center">Full present tense:</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-700">
                  {Object.entries(exercise.verb.present).map(([p, form]) => (
                    <div key={p} className={p === exercise.person ? 'font-bold text-purple-700' : ''}>
                      {p}: {form}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {exercise.exerciseType === 'irregular' && (
              <div className="bg-white/60 rounded-xl p-3">
                <div className="text-xs text-gray-500 mb-2 text-center">All irregular forms:</div>
                <div className="space-y-1 text-sm text-gray-700">
                  <div className={exercise.tenseQuestion === 'pastSingular' ? 'font-bold text-orange-700' : ''}>
                    Verleden (enk.): {exercise.verb.pastSingular}
                  </div>
                  <div className={exercise.tenseQuestion === 'pastPlural' ? 'font-bold text-orange-700' : ''}>
                    Verleden (mv.): {exercise.verb.pastPlural}
                  </div>
                  <div className={exercise.tenseQuestion === 'pastParticiple' ? 'font-bold text-orange-700' : ''}>
                    Volt. deelw.: {exercise.verb.pastParticiple}
                  </div>
                  <div className="text-gray-500">Hulpwerkwoord: {exercise.verb.auxiliary}</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Session stats */}
      <div className="text-center text-sm text-gray-600">
        Session: ✓ {sessionStats.correct} · ✗ {sessionStats.incorrect} · Total: {sessionStats.total}
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={onNext}
          className="flex-1 bg-purple-600 text-white py-3 px-6 rounded-xl font-medium text-lg hover:bg-purple-700 transition-colors"
        >
          Next →
        </button>
        <button
          onClick={onStop}
          className="bg-gray-200 text-gray-700 py-3 px-4 rounded-xl hover:bg-gray-300 transition-colors"
        >
          Stop
        </button>
      </div>
    </div>
  );
}
